import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Button,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    InputNumber,
    Checkbox,
    message,
    Tag,
    Space,
    Typography,
    Tabs,
    Popconfirm,
    Tooltip,
    Descriptions,
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    PlusOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    FileTextOutlined,
    ThunderboltOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { loadLeaveTypes } from 'store/slices/MasterSlice';
import {
    loadLeaveApplications,
    createLeaveApplication,
    updateLeaveApplication,
    approveLeaveApplication,
    rejectLeaveApplication,
    loadLeaveBalances,
    loadLMHolidays,
    createLMHoliday,
    updateLMHoliday,
    deleteLMHoliday,
    expandHolidays,
    createLeaveType,
    updateLeaveType,
    deleteLeaveType,
    loadLeaveEncashments,
    createLeaveEncashment,
    approveLeaveEncashment,
    rejectLeaveEncashment,
    generateLeaveCredits,
    loadLeaveCreditLogs,
    loadCompOffBalances,
    loadEmployees,
} from 'store/actions/AuthActions';
import useIsMobile from 'hooks/useIsMobile';
import MobileFilterDrawer, { MobileFilterButton } from 'components/Common/MobileFilterDrawer';
import useUrlFilters from 'hooks/useUrlFilters';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

// ─── RRULE Builder Helper ─────────────────────────────────────────────────────
const DAYS_OF_WEEK = [
    { label: 'Mon', value: 'MO' },
    { label: 'Tue', value: 'TU' },
    { label: 'Wed', value: 'WE' },
    { label: 'Thu', value: 'TH' },
    { label: 'Fri', value: 'FR' },
    { label: 'Sat', value: 'SA' },
    { label: 'Sun', value: 'SU' },
];

const MONTHS = [
    { label: 'January', value: 1 }, { label: 'February', value: 2 },
    { label: 'March', value: 3 }, { label: 'April', value: 4 },
    { label: 'May', value: 5 }, { label: 'June', value: 6 },
    { label: 'July', value: 7 }, { label: 'August', value: 8 },
    { label: 'September', value: 9 }, { label: 'October', value: 10 },
    { label: 'November', value: 11 }, { label: 'December', value: 12 },
];

const NTH_OPTIONS = [
    { label: '1st', value: '1' }, { label: '2nd', value: '2' },
    { label: '3rd', value: '3' }, { label: '4th', value: '4' },
    { label: 'Last', value: '-1' },
];

const buildRrule = (freq, { byMonth, byMonthDay, byDay, nthWeekdays, weekdays }) => {
    const parts = [`FREQ=${freq}`];
    if (freq === 'YEARLY') {
        if (byMonth) parts.push(`BYMONTH=${byMonth}`);
        if (nthWeekdays?.length && byDay) {
            parts.push(`BYDAY=${nthWeekdays.map(n => `${n}${byDay}`).join(',')}`);
        } else if (byMonthDay) {
            parts.push(`BYMONTHDAY=${byMonthDay}`);
        }
    } else if (freq === 'MONTHLY') {
        if (nthWeekdays?.length && byDay) {
            parts.push(`BYDAY=${nthWeekdays.map(n => `${n}${byDay}`).join(',')}`);
        } else if (byMonthDay) {
            parts.push(`BYMONTHDAY=${byMonthDay}`);
        }
    } else if (freq === 'WEEKLY') {
        if (weekdays?.length) parts.push(`BYDAY=${weekdays.join(',')}`);
    }
    return parts.join(';');
};

const RRuleBuilder = ({ value, onChange }) => {
    const [freq, setFreq] = useState('YEARLY');
    const [byMonth, setByMonth] = useState(null);
    const [byMonthDay, setByMonthDay] = useState(null);
    const [byDay, setByDay] = useState(null);
    const [nthWeekdays, setNthWeekdays] = useState([]);
    const [weekdays, setWeekdays] = useState([]);
    const [useNthDay, setUseNthDay] = useState(false);
    const [manualEdit, setManualEdit] = useState(false);

    const updateRule = useCallback(() => {
        if (manualEdit) return;
        const rule = buildRrule(freq, { byMonth, byMonthDay, byDay, nthWeekdays, weekdays });
        onChange?.(rule);
    }, [freq, byMonth, byMonthDay, byDay, nthWeekdays, weekdays, manualEdit, onChange]);

    useEffect(() => { updateRule(); }, [updateRule]);

    if (manualEdit) {
        return (
            <div>
                <Input
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    placeholder="e.g. FREQ=YEARLY;BYMONTH=1;BYMONTHDAY=26"
                />
                <Button type="link" size="small" onClick={() => setManualEdit(false)}>
                    Switch to builder
                </Button>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Select value={freq} onChange={setFreq} style={{ width: 120 }}>
                    <Option value="YEARLY">Yearly</Option>
                    <Option value="MONTHLY">Monthly</Option>
                    <Option value="WEEKLY">Weekly</Option>
                </Select>

                {(freq === 'YEARLY') && (
                    <Select
                        value={byMonth}
                        onChange={setByMonth}
                        placeholder="Month"
                        style={{ width: 130 }}
                        allowClear
                    >
                        {MONTHS.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
                    </Select>
                )}

                {(freq === 'YEARLY' || freq === 'MONTHLY') && (
                    <>
                        <Checkbox checked={useNthDay} onChange={(e) => setUseNthDay(e.target.checked)}>
                            Nth weekday
                        </Checkbox>
                        {useNthDay ? (
                            <>
                                <Select
                                    mode="multiple"
                                    value={nthWeekdays}
                                    onChange={setNthWeekdays}
                                    placeholder="Which"
                                    style={{ minWidth: 120 }}
                                >
                                    {NTH_OPTIONS.map(n => <Option key={n.value} value={n.value}>{n.label}</Option>)}
                                </Select>
                                <Select value={byDay} onChange={setByDay} placeholder="Day" style={{ width: 90 }}>
                                    {DAYS_OF_WEEK.map(d => <Option key={d.value} value={d.value}>{d.label}</Option>)}
                                </Select>
                            </>
                        ) : (
                            <InputNumber
                                value={byMonthDay}
                                onChange={setByMonthDay}
                                min={1}
                                max={31}
                                placeholder="Day of month"
                                style={{ width: 130 }}
                            />
                        )}
                    </>
                )}

                {freq === 'WEEKLY' && (
                    <Checkbox.Group
                        options={DAYS_OF_WEEK.map(d => ({ label: d.label, value: d.value }))}
                        value={weekdays}
                        onChange={setWeekdays}
                    />
                )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>RRULE: {value || '—'}</Text>
                <Button type="link" size="small" onClick={() => setManualEdit(true)}>
                    Edit manually
                </Button>
            </div>
        </div>
    );
};


// ─── Status helpers ───────────────────────────────────────────────────────────
const getStatusColor = (s) => {
    const map = { pending: 'orange', approved: 'green', rejected: 'red', cancelled: 'gray', withdrawn: 'default', processed: 'blue' };
    return map[s] || 'blue';
};

const HOLIDAY_TYPE_LABELS = {
    weekly_off: 'Weekly Off',
    national: 'National',
    company: 'Company',
    regional: 'Regional',
    religious: 'Religious',
};

const LEAVE_CATEGORY_LABELS = {
    paid: 'Paid', unpaid: 'Unpaid', comp: 'Compensatory', maternity: 'Maternity',
    paternity: 'Paternity', sick: 'Sick', casual: 'Casual', earned: 'Earned',
    flexi: 'Flexi', emergency: 'Emergency', bereavement: 'Bereavement',
};


// ─── Main Component ───────────────────────────────────────────────────────────
// Active tab, filters and pagination live in the URL so a view can be shared as a link.
// Years have no schema default: the fallback is "now", and a link must keep its year.
const URL_FILTERS = {
    tab: { type: 'string', default: 'applications' },
    status: { type: 'string' },
    leaveType: { type: 'string', key: 'leave_type' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 15 },
    holidayYear: { type: 'number', key: 'holiday_year' },
    holidayType: { type: 'string', key: 'holiday_type' },
    holPage: { type: 'number', key: 'hol_page', default: 1 },
    holPageSize: { type: 'number', key: 'hol_page_size', default: 100 },
    encPage: { type: 'number', key: 'enc_page', default: 1 },
    encPageSize: { type: 'number', key: 'enc_page_size', default: 15 },
    balYear: { type: 'number', key: 'bal_year' },
    logYear: { type: 'number', key: 'log_year' },
    logPage: { type: 'number', key: 'log_page', default: 1 },
    logPageSize: { type: 'number', key: 'log_page_size', default: 15 },
};

const LeaveManagement = () => {
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const leaveTypes = useSelector((state) => state.master?.leave_types) || [];
    const user = useSelector((state) => state.auth.user);
    const [filters, setFilters] = useUrlFilters(URL_FILTERS);
    const urlSetter = (name) => (value) => setFilters({ [name]: value });
    // Page sizes from the URL are clamped to what the API will return in one page
    const appPageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
    const holPageSize = Math.min(filters.holPageSize, MAX_PAGE_SIZE);
    const encPageSize = Math.min(filters.encPageSize, MAX_PAGE_SIZE);
    const logPageSize = Math.min(filters.logPageSize, MAX_PAGE_SIZE);
    // Back to page 1 when the page size changes
    const pageSetter = (pageKey, sizeKey, size) => (p, ps) =>
        setFilters({ [pageKey]: ps !== size ? 1 : p, [sizeKey]: ps });
    const activeTab = filters.tab;

    // Data states
    const [applications, setApplications] = useState([]);
    const [appLoading, setAppLoading] = useState(false);
    const [appPagination, setAppPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    const [balances, setBalances] = useState([]);
    const [balLoading, setBalLoading] = useState(false);

    const [holidays, setHolidays] = useState([]);
    const [holLoading, setHolLoading] = useState(false);
    const [holPagination, setHolPagination] = useState({ current: 1, pageSize: 100, total: 0 });
    const holidayYear = filters.holidayYear || dayjs().year();
    const selectedHolidayType = filters.holidayType;
    const setSelectedHolidayType = urlSetter('holidayType');

    const [encashments, setEncashments] = useState([]);
    const [encLoading, setEncLoading] = useState(false);
    const [encPagination, setEncPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    // Credit generation states
    const [creditYear, setCreditYear] = useState(dayjs().year());
    const [creditMonth, setCreditMonth] = useState(dayjs().month() + 1);
    const [creditLoading, setCreditLoading] = useState(false);
    const balYear = filters.balYear || dayjs().year();
    const setBalYear = urlSetter('balYear');

    // Credit log states
    const [creditLogs, setCreditLogs] = useState([]);
    const [creditLogLoading, setCreditLogLoading] = useState(false);
    const [creditLogPagination, setCreditLogPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const creditLogYear = filters.logYear || dayjs().year();

    // Employees (for createApp employee selector)
    const [employees, setEmployees] = useState([]);

    // Modal states
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(''); // createApp, editApp, viewApp, createType, editType, createHoliday, editHoliday, createEncashment
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);

    // View application states
    const [viewEmpBalances, setViewEmpBalances] = useState([]);
    const [viewEmpBalLoading, setViewEmpBalLoading] = useState(false);
    const [viewEmpCompOffAvail, setViewEmpCompOffAvail] = useState(null);
    const [actionReason, setActionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Filter states for applications
    const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
    const statusFilter = filters.status;
    const setStatusFilter = urlSetter('status');
    // Leave type ids come back from the URL as strings; match them to the real id
    const leaveTypeFilter = filters.leaveType
        ? (leaveTypes.find(t => String(t.id) === filters.leaveType)?.id ?? filters.leaveType)
        : null;
    const setLeaveTypeFilter = urlSetter('leaveType');

    // Stats
    const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

    // ─── Data fetching ────────────────────────────────────────────────
    const fetchApplications = useCallback(async (page = 1, pageSize = 15) => {
        setAppLoading(true);
        try {
            const res = await loadLeaveApplications(page, null, pageSize);
            if (res?.data) {
                const results = res.data.results || res.data;
                setApplications(Array.isArray(results) ? results : []);
                const total = res.data.count || (Array.isArray(results) ? results.length : 0);
                setAppPagination({ current: page, pageSize, total });
                // calc stats from current page (or all if not paginated)
                const all = Array.isArray(results) ? results : [];
                setStats({
                    total: total,
                    pending: all.filter(a => a.status === 'pending').length,
                    approved: all.filter(a => a.status === 'approved').length,
                    rejected: all.filter(a => a.status === 'rejected').length,
                });
            }
        } catch { /* handled by API */ }
        setAppLoading(false);
    }, []);

    const fetchBalances = useCallback(async (year) => {
        setBalLoading(true);
        try {
            const res = await loadLeaveBalances(1, 500, year || balYear);
            if (res?.data) {
                const results = res.data.results || res.data;
                setBalances(Array.isArray(results) ? results : []);
            }
        } catch { /* */ }
        setBalLoading(false);
    }, [balYear]);

    const fetchHolidays = useCallback(async (page = 1, pageSize = 100, year) => {
        setHolLoading(true);
        try {
            const res = await loadLMHolidays(page, pageSize, { year: year || holidayYear });
            if (res?.data) {
                const results = res.data.results || res.data;
                setHolidays(Array.isArray(results) ? results : []);
                setHolPagination({ current: page, pageSize, total: res.data.count || 0 });
            }
        } catch { /* */ }
        setHolLoading(false);
    }, [holidayYear]);

    const fetchEncashments = useCallback(async (page = 1, pageSize = 15) => {
        setEncLoading(true);
        try {
            const res = await loadLeaveEncashments(page, pageSize);
            if (res?.data) {
                const results = res.data.results || res.data;
                setEncashments(Array.isArray(results) ? results : []);
                setEncPagination({ current: page, pageSize, total: res.data.count || 0 });
            }
        } catch { /* */ }
        setEncLoading(false);
    }, []);

    const fetchCreditLogs = useCallback(async (page = 1, pageSize = 15, year) => {
        setCreditLogLoading(true);
        try {
            const res = await loadLeaveCreditLogs(page, pageSize, { year: year || creditLogYear });
            if (res?.data) {
                const results = res.data.results || res.data;
                setCreditLogs(Array.isArray(results) ? results : []);
                setCreditLogPagination({ current: page, pageSize, total: res.data.count || 0 });
            }
        } catch { /* */ }
        setCreditLogLoading(false);
    }, [creditLogYear]);

    useEffect(() => {
        fetchApplications(filters.page, appPageSize);
    }, [fetchApplications, filters.page, appPageSize]);

    // Other tabs load lazily, when opened, from the URL state
    useEffect(() => {
        if (activeTab === 'holidays') fetchHolidays(filters.holPage, holPageSize, holidayYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filters.holPage, holPageSize, holidayYear]);

    useEffect(() => {
        if (activeTab === 'encashment') fetchEncashments(filters.encPage, encPageSize);
    }, [activeTab, fetchEncashments, filters.encPage, encPageSize]);

    useEffect(() => {
        if (activeTab === 'balances') fetchBalances(balYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, balYear]);

    useEffect(() => {
        if (activeTab === 'creditLogs') fetchCreditLogs(filters.logPage, logPageSize, creditLogYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filters.logPage, logPageSize, creditLogYear]);

    useEffect(() => {
        dispatch(loadLeaveTypes());
        (async () => {
            const res = await loadEmployees(1, '', 1000);
            if (res?.results) setEmployees(res.results);
        })();
    }, [dispatch]);

    // ─── Modal helpers ────────────────────────────────────────────────
    const fetchEmployeeBalances = async (employeeId, year) => {
        setViewEmpBalLoading(true);
        try {
            const res = await loadLeaveBalances(1, 500, year || dayjs().year(), employeeId);
            if (res?.data) {
                const results = res.data.results || res.data;
                setViewEmpBalances(Array.isArray(results) ? results : []);
            }
        } catch { /* */ }
        setViewEmpBalLoading(false);

        // Fetch comp-off balance for this employee across all years so admin
        // can see available credits before approving a Comp-Off leave.
        try {
            const data = await loadCompOffBalances(employeeId);
            const rows = data?.results || data || [];
            const total = (Array.isArray(rows) ? rows : []).reduce((acc, r) => {
                const avail = parseFloat(r.available_days ?? 0);
                return acc + (Number.isFinite(avail) ? avail : 0);
            }, 0);
            setViewEmpCompOffAvail(total);
        } catch {
            setViewEmpCompOffAvail(null);
        }
    };

    const openModal = (type, record = null) => {
        setModalType(type);
        setSelectedRecord(record);
        setModalVisible(true);
        setActionReason('');
        form.resetFields();

        if (record) {
            if (type === 'viewApp') {
                fetchEmployeeBalances(record.employee, dayjs(record.start_date).year());
            } else if (type === 'editApp') {
                form.setFieldsValue({
                    ...record,
                    leave_dates: [dayjs(record.start_date), dayjs(record.end_date)],
                    leave_type: record.leave_type,
                });
            } else if (type === 'editType') {
                form.setFieldsValue(record);
            } else if (type === 'editHoliday') {
                form.setFieldsValue({
                    ...record,
                    date: dayjs(record.date),
                });
            }
        }
    };

    const closeModal = () => {
        setModalVisible(false);
        setModalType('');
        setSelectedRecord(null);
        setViewEmpBalances([]);
        setViewEmpCompOffAvail(null);
        setActionReason('');
        form.resetFields();
    };

    // ─── Submit handlers ──────────────────────────────────────────────
    const handleSubmit = async (values) => {
        setSaving(true);
        try {
            if (modalType === 'createApp' || modalType === 'editApp') {
                const employeeId = modalType === 'editApp' && selectedRecord
                    ? selectedRecord.employee
                    : values.employee;
                const data = {
                    employee: employeeId,
                    leave_type: values.leave_type,
                    start_date: values.leave_dates[0].format('YYYY-MM-DD'),
                    end_date: values.leave_dates[1].format('YYYY-MM-DD'),
                    reason: values.reason,
                    leave_duration: values.leave_duration || 'full_day',
                    priority: values.priority || 'normal',
                    contact_number: values.contact_number || '',
                };
                if (modalType === 'editApp' && selectedRecord) {
                    await updateLeaveApplication(selectedRecord.id, data);
                    message.success('Leave application updated');
                } else {
                    await createLeaveApplication(data);
                    message.success('Leave application created');
                }
                fetchApplications(appPagination.current, appPagination.pageSize);
            } else if (modalType === 'createType' || modalType === 'editType') {
                const data = {
                    name: values.name,
                    category: values.category,
                    description: values.description || '',
                    max_days_per_year: values.max_days_per_year,
                    monthly_credit_days: values.monthly_credit_days || 0,
                    max_consecutive_days: values.max_consecutive_days || 30,
                    max_application_days: values.max_application_days || 48,
                    min_notice_days: values.min_notice_days || 1,
                    is_carry_forward_allowed: values.is_carry_forward_allowed || false,
                    max_carry_forward_days: values.max_carry_forward_days || 0,
                    max_accumulation_days: values.max_accumulation_days || 0,
                    is_encashable: values.is_encashable || false,
                    max_encashable_days: values.max_encashable_days || 0,
                    allows_half_day: values.allows_half_day || false,
                };
                if (modalType === 'editType' && selectedRecord) {
                    await updateLeaveType(selectedRecord.id, data);
                    message.success('Leave type updated');
                } else {
                    await createLeaveType(data);
                    message.success('Leave type created');
                }
                dispatch(loadLeaveTypes());
            } else if (modalType === 'createHoliday' || modalType === 'editHoliday') {
                const data = {
                    name: values.name,
                    date: values.date.format('YYYY-MM-DD'),
                    holiday_type: values.holiday_type,
                    description: values.description || '',
                    is_recurring: values.is_recurring || false,
                    recurrence_rule: values.is_recurring ? (values.recurrence_rule || '') : '',
                    is_active: true,
                };
                if (modalType === 'editHoliday' && selectedRecord) {
                    await updateLMHoliday(selectedRecord.id, data);
                    message.success('Holiday updated');
                } else {
                    await createLMHoliday(data);
                    message.success('Holiday created');
                }
                fetchHolidays(holPagination.current, holPagination.pageSize);
            } else if (modalType === 'createEncashment') {
                const data = {
                    employee: values.employee,
                    leave_type: values.leave_type,
                    year: values.year,
                    days_to_encash: values.days_to_encash,
                    rate_per_day: values.rate_per_day,
                    reason: values.reason,
                };
                await createLeaveEncashment(data);
                message.success('Leave encashment request created');
                fetchEncashments(encPagination.current, encPagination.pageSize);
            }
            closeModal();
        } catch (err) {
            const detail = err?.response?.data;
            if (detail && typeof detail === 'object') {
                const msgs = Object.values(detail).flat().join('; ');
                message.error(msgs || 'Operation failed');
            } else {
                message.error('Operation failed');
            }
        }
        setSaving(false);
    };

    // ─── Approve/Reject handlers ─────────────────────────────────────
    const handleApprove = async (id, reason) => {
        if (!reason || !reason.trim()) {
            message.warning('Please enter a reason for approval');
            return;
        }
        setActionLoading(true);
        try {
            await approveLeaveApplication(id, reason.trim());
            message.success('Leave application approved');
            closeModal();
            fetchApplications(appPagination.current, appPagination.pageSize);
        } catch (err) {
            const msg = err?.response?.data?.error
                || err?.response?.data?.detail
                || 'Failed to approve';
            message.error(msg);
        }
        setActionLoading(false);
    };

    const handleReject = async (id, reason) => {
        if (!reason || !reason.trim()) {
            message.warning('Please enter a reason for rejection');
            return;
        }
        setActionLoading(true);
        try {
            await rejectLeaveApplication(id, reason.trim());
            message.success('Leave application rejected');
            closeModal();
            fetchApplications(appPagination.current, appPagination.pageSize);
        } catch (err) {
            const msg = err?.response?.data?.error
                || err?.response?.data?.detail
                || 'Failed to reject';
            message.error(msg);
        }
        setActionLoading(false);
    };

    const handleDeleteLeaveType = async (id) => {
        try {
            await deleteLeaveType(id);
            message.success('Leave type deleted');
            dispatch(loadLeaveTypes());
        } catch {
            message.error('Failed to delete leave type');
        }
    };

    const handleDeleteHoliday = async (id) => {
        try {
            await deleteLMHoliday(id);
            message.success('Holiday deleted');
            fetchHolidays(holPagination.current, holPagination.pageSize);
        } catch {
            message.error('Failed to delete holiday');
        }
    };

    const handleExpandHolidays = async () => {
        try {
            const result = await expandHolidays(holidayYear);
            message.success(result.message || 'Holidays expanded');
            fetchHolidays(1, holPagination.pageSize, holidayYear);
        } catch {
            message.error('Failed to expand holidays');
        }
    };

    const handleApproveEncashment = async (id) => {
        try {
            await approveLeaveEncashment(id);
            message.success('Encashment approved');
            fetchEncashments(encPagination.current, encPagination.pageSize);
        } catch {
            message.error('Failed to approve encashment');
        }
    };

    const handleRejectEncashment = async (id) => {
        try {
            await rejectLeaveEncashment(id, '');
            message.success('Encashment rejected');
            fetchEncashments(encPagination.current, encPagination.pageSize);
        } catch {
            message.error('Failed to reject encashment');
        }
    };

    const handleGenerateCredits = async () => {
        setCreditLoading(true);
        try {
            const result = await generateLeaveCredits(creditYear, creditMonth);
            message.success(
                `Credits generated: ${result.balances_credited} balances updated, ` +
                `${result.employees_processed} employees processed, ` +
                `${result.employees_skipped} skipped, ` +
                `${result.logs_created || 0} log entries created`
            );
            // Sync balance year to credit year so user sees the generated records
            // (a year change refetches via the URL effect)
            if (creditYear === balYear) fetchBalances(creditYear);
            else setBalYear(creditYear);
        } catch (err) {
            const detail = err?.response?.data;
            message.error(detail?.error || 'Failed to generate credits');
        }
        setCreditLoading(false);
    };

    // ─── Filter helpers ───────────────────────────────────────────────
    const getActiveFiltersCount = () => (statusFilter ? 1 : 0) + (leaveTypeFilter ? 1 : 0);
    const handleClearFilters = () => { setFilters({ status: null, leaveType: null }); };

    const filteredApplications = applications.filter(a => {
        if (statusFilter && a.status !== statusFilter) return false;
        if (leaveTypeFilter && a.leave_type !== leaveTypeFilter) return false;
        return true;
    });

    // ─── Column definitions ───────────────────────────────────────────

    // Applications columns
    const appColumns = [
        {
            title: 'Employee',
            key: 'employee',
            render: (_, r) => <Text strong>{r.employee_name || '—'}</Text>,
        },
        {
            title: 'Leave Type',
            dataIndex: 'leave_type_name',
            key: 'leave_type_name',
        },
        {
            title: 'Duration',
            key: 'duration',
            width: 320,
            render: (_, r) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {dayjs(r.start_date).format('DD MMM YYYY')} - {dayjs(r.end_date).format('DD MMM YYYY')}{' '}
                    <Text type="secondary">({r.total_days} day(s))</Text>
                </span>
            ),
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (s) => <Tag color={getStatusColor(s)}>{(s || '').toUpperCase()}</Tag>,
        },
        {
            title: 'Applied',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (d) => d ? dayjs(d).format('DD MMM YYYY') : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, r) => (
                <Space>
                    <Tooltip title="View">
                        <Button icon={<EyeOutlined />} size="small" onClick={() => openModal('viewApp', r)} />
                    </Tooltip>
                    {r.status === 'pending' && (
                        <Tooltip title="Edit">
                            <Button icon={<EditOutlined />} size="small" onClick={() => openModal('editApp', r)} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    // Leave types columns
    const typeColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Category', dataIndex: 'category', key: 'category', render: (c) => LEAVE_CATEGORY_LABELS[c] || c },
        { title: 'Max Days/Year', dataIndex: 'max_days_per_year', key: 'max_days_per_year' },
        { title: 'Monthly Credit', dataIndex: 'monthly_credit_days', key: 'monthly_credit_days' },
        { title: 'Carry Forward', dataIndex: 'is_carry_forward_allowed', key: 'cf', render: (v) => v ? 'Yes' : 'No' },
        { title: 'Encashable', dataIndex: 'is_encashable', key: 'enc', render: (v) => v ? 'Yes' : 'No' },
        { title: 'Half Day', dataIndex: 'allows_half_day', key: 'hd', render: (v) => v ? 'Yes' : 'No' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, r) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => openModal('editType', r)} />
                    <Popconfirm title="Delete this leave type?" onConfirm={() => handleDeleteLeaveType(r.id)}>
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Holiday columns
    const holColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => dayjs(d).format('DD MMM YYYY') },
        { title: 'Type', dataIndex: 'holiday_type', key: 'holiday_type', render: (t) => HOLIDAY_TYPE_LABELS[t] || t },
        { title: 'Recurring', dataIndex: 'is_recurring', key: 'is_recurring', render: (v) => v ? <Tag color="blue">Yes</Tag> : 'No' },
        { title: 'RRULE', dataIndex: 'recurrence_rule', key: 'recurrence_rule', ellipsis: true, render: (v) => v || '—' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, r) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => openModal('editHoliday', r)} />
                    <Popconfirm title="Delete this holiday?" onConfirm={() => handleDeleteHoliday(r.id)}>
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // Encashment columns
    const encColumns = [
        { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
        { title: 'Leave Type', dataIndex: 'leave_type_name', key: 'leave_type_name' },
        { title: 'Year', dataIndex: 'year', key: 'year' },
        { title: 'Days', dataIndex: 'days_to_encash', key: 'days_to_encash' },
        { title: 'Rate/Day', dataIndex: 'rate_per_day', key: 'rate_per_day' },
        { title: 'Total', dataIndex: 'total_amount', key: 'total_amount' },
        { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color={getStatusColor(s)}>{(s || '').toUpperCase()}</Tag> },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, r) => (
                <Space>
                    {r.status === 'pending' && (
                        <>
                            <Popconfirm title="Approve?" onConfirm={() => handleApproveEncashment(r.id)}>
                                <Button icon={<CheckCircleOutlined />} size="small" type="primary" />
                            </Popconfirm>
                            <Popconfirm title="Reject?" onConfirm={() => handleRejectEncashment(r.id)}>
                                <Button icon={<CloseCircleOutlined />} size="small" danger />
                            </Popconfirm>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    // Balance columns
    const balColumns = [
        { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
        { title: 'Leave Type', dataIndex: 'leave_type_name', key: 'leave_type_name' },
        { title: 'Year', dataIndex: 'year', key: 'year' },
        { title: 'Allocated', dataIndex: 'allocated_days', key: 'allocated_days' },
        { title: 'Used', dataIndex: 'used_days', key: 'used_days' },
        { title: 'Carried Fwd', dataIndex: 'carried_forward_days', key: 'carried_forward_days' },
        { title: 'Encashed', dataIndex: 'encashed_days', key: 'encashed_days' },
        { title: 'Available', dataIndex: 'available_days', key: 'available_days', render: (v) => <Text strong>{v}</Text> },
        {
            title: 'Credited Through',
            dataIndex: 'credited_through_month',
            key: 'credited_through_month',
            render: (v) => v ? MONTHS.find(m => m.value === v)?.label || `Month ${v}` : '—',
        },
        {
            title: 'Last Credited At',
            dataIndex: 'last_credited_at',
            key: 'last_credited_at',
            render: (v) => v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—',
        },
    ];

    // ─── Tab items ────────────────────────────────────────────────────
    const tabItems = [
        {
            key: 'applications',
            label: 'Leave Applications',
            children: (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        {isMobile ? (
                            <MobileFilterButton onClick={() => setFilterDrawerVisible(true)} activeFiltersCount={getActiveFiltersCount()} size="small" />
                        ) : (
                            <Space>
                                <Select placeholder="Status" style={{ width: 130 }} allowClear value={statusFilter} onChange={setStatusFilter}>
                                    <Option value="pending">Pending</Option>
                                    <Option value="approved">Approved</Option>
                                    <Option value="rejected">Rejected</Option>
                                </Select>
                                <Select placeholder="Leave Type" style={{ width: 180 }} allowClear value={leaveTypeFilter} onChange={setLeaveTypeFilter}>
                                    {leaveTypes.map(t => <Option key={t.id} value={t.id}>{t.name}</Option>)}
                                </Select>
                            </Space>
                        )}
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal('createApp')} size={isMobile ? 'small' : 'middle'}>
                            {isMobile ? 'New' : 'New Leave Application'}
                        </Button>
                    </div>
                    {isMobile && (
                        <MobileFilterDrawer visible={filterDrawerVisible} onClose={() => setFilterDrawerVisible(false)} onClear={handleClearFilters} title="Leave Filters" activeFiltersCount={getActiveFiltersCount()}>
                            <Form layout="vertical">
                                <Form.Item label="Status">
                                    <Select placeholder="Status" style={{ width: '100%' }} allowClear value={statusFilter} onChange={setStatusFilter}>
                                        <Option value="pending">Pending</Option>
                                        <Option value="approved">Approved</Option>
                                        <Option value="rejected">Rejected</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Leave Type">
                                    <Select placeholder="Leave Type" style={{ width: '100%' }} allowClear value={leaveTypeFilter} onChange={setLeaveTypeFilter}>
                                        {leaveTypes.map(t => <Option key={t.id} value={t.id}>{t.name}</Option>)}
                                    </Select>
                                </Form.Item>
                            </Form>
                        </MobileFilterDrawer>
                    )}
                    <Table
                        columns={appColumns}
                        dataSource={filteredApplications}
                        loading={appLoading}
                        rowKey="id"
                        pagination={{
                            ...appPagination,
                            showSizeChanger: !isMobile,
                            showQuickJumper: !isMobile,
                            simple: isMobile,
                            size: isMobile ? 'small' : 'default',
                            onChange: pageSetter('page', 'pageSize', appPageSize),
                        }}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 900 }}
                    />
                </div>
            ),
        },
        {
            key: 'types',
            label: 'Leave Types',
            children: (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal('createType')}>
                            Add Leave Type
                        </Button>
                    </div>
                    <Table
                        columns={typeColumns}
                        dataSource={leaveTypes}
                        rowKey="id"
                        pagination={false}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 800 }}
                    />
                </div>
            ),
        },
        {
            key: 'holidays',
            label: 'Holidays',
            children: (() => {
                const typeCounts = {};
                holidays.forEach(h => {
                    const key = h.holiday_type || 'other';
                    typeCounts[key] = (typeCounts[key] || 0) + 1;
                });
                const HOLIDAY_STAT_CONFIG = [
                    { key: 'weekly_off', label: 'Weekly Off', color: '#722ed1' },
                    { key: 'national', label: 'National', color: '#fa8c16' },
                    { key: 'company', label: 'Company', color: '#1890ff' },
                    { key: 'regional', label: 'Regional', color: '#13c2c2' },
                    { key: 'religious', label: 'Religious', color: '#52c41a' },
                ];
                const filteredHolidays = selectedHolidayType
                    ? holidays.filter(h => (h.holiday_type || 'other') === selectedHolidayType)
                    : holidays;
                const toggleHolidayType = (key) =>
                    setSelectedHolidayType(selectedHolidayType === key ? null : key);
                const cardStyle = (isSelected, color) => ({
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderColor: isSelected ? (color || '#333') : undefined,
                    boxShadow: isSelected ? `0 0 0 2px ${color || '#333'}` : undefined,
                    transition: 'box-shadow 0.15s, border-color 0.15s',
                });
                return (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <DatePicker
                            picker="year"
                            value={dayjs().year(holidayYear)}
                            onChange={(val) => {
                                const y = val ? val.year() : dayjs().year();
                                setFilters({ holidayYear: y, holPage: 1 });
                            }}
                            style={{ width: 120 }}
                            allowClear={false}
                        />
                        <Space>
                            <Popconfirm title={`Expand recurring holidays for ${holidayYear}?`} onConfirm={handleExpandHolidays}>
                                <Button icon={<ThunderboltOutlined />}>Expand Holidays ({holidayYear})</Button>
                            </Popconfirm>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal('createHoliday')}>
                                Add Holiday
                            </Button>
                        </Space>
                    </div>
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={8} md={4}>
                            <Card
                                size="small"
                                style={cardStyle(selectedHolidayType === null, '#333')}
                                onClick={() => setSelectedHolidayType(null)}
                            >
                                <Statistic title="Total" value={holidays.length} valueStyle={{ fontSize: 20, color: '#333' }} />
                            </Card>
                        </Col>
                        {HOLIDAY_STAT_CONFIG.map(s => (
                            <Col xs={12} sm={8} md={4} key={s.key}>
                                <Card
                                    size="small"
                                    style={cardStyle(selectedHolidayType === s.key, s.color)}
                                    onClick={() => toggleHolidayType(s.key)}
                                >
                                    <Statistic title={s.label} value={typeCounts[s.key] || 0} valueStyle={{ fontSize: 20, color: s.color }} />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    <Table
                        columns={holColumns}
                        dataSource={filteredHolidays}
                        loading={holLoading}
                        rowKey="id"
                        pagination={{
                            ...holPagination,
                            showSizeChanger: !isMobile,
                            simple: isMobile,
                            onChange: pageSetter('holPage', 'holPageSize', holPageSize),
                        }}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 800 }}
                    />
                </div>
                );
            })(),
        },
        {
            key: 'encashment',
            label: 'Leave Encashment',
            children: (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal('createEncashment')}>
                            New Encashment Request
                        </Button>
                    </div>
                    <Table
                        columns={encColumns}
                        dataSource={encashments}
                        loading={encLoading}
                        rowKey="id"
                        pagination={{
                            ...encPagination,
                            showSizeChanger: !isMobile,
                            simple: isMobile,
                            onChange: pageSetter('encPage', 'encPageSize', encPageSize),
                        }}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 800 }}
                    />
                </div>
            ),
        },
        {
            key: 'balances',
            label: 'Leave Balances',
            children: (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <Space wrap>
                            <Text strong>Year:</Text>
                            <DatePicker
                                picker="year"
                                value={dayjs().year(balYear)}
                                onChange={(val) => {
                                    const y = val ? val.year() : dayjs().year();
                                    setBalYear(y);
                                }}
                                style={{ width: 100 }}
                                allowClear={false}
                            />
                        </Space>
                        <Space wrap>
                            <Text strong>Generate Credits:</Text>
                            <Select
                                value={creditMonth}
                                onChange={setCreditMonth}
                                style={{ width: 130 }}
                            >
                                {MONTHS.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
                            </Select>
                            <DatePicker
                                picker="year"
                                value={dayjs().year(creditYear)}
                                onChange={(val) => setCreditYear(val ? val.year() : dayjs().year())}
                                style={{ width: 100 }}
                                allowClear={false}
                            />
                            <Popconfirm
                                title={`Generate monthly leave credits through ${MONTHS.find(m => m.value === creditMonth)?.label} ${creditYear}?`}
                                description="This will credit leaves for all active employees based on their joining date and leave type configuration."
                                onConfirm={handleGenerateCredits}
                            >
                                <Button
                                    type="primary"
                                    icon={<ThunderboltOutlined />}
                                    loading={creditLoading}
                                >
                                    Generate
                                </Button>
                            </Popconfirm>
                        </Space>
                    </div>
                    <Table
                        columns={balColumns}
                        dataSource={balances}
                        loading={balLoading}
                        rowKey="id"
                        pagination={false}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 900 }}
                    />
                </div>
            ),
        },
        {
            key: 'creditLogs',
            label: 'Credit Logs',
            children: (
                <div>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <Space wrap>
                            <Text strong>Year:</Text>
                            <DatePicker
                                picker="year"
                                value={dayjs().year(creditLogYear)}
                                onChange={(val) => {
                                    const y = val ? val.year() : dayjs().year();
                                    setFilters({ logYear: y, logPage: 1 });
                                }}
                                style={{ width: 100 }}
                                allowClear={false}
                            />
                        </Space>
                    </div>
                    <Table
                        columns={[
                            { title: 'Employee', dataIndex: 'employee_name', key: 'employee_name' },
                            { title: 'Leave Type', dataIndex: 'leave_type_name', key: 'leave_type_name' },
                            { title: 'Year', dataIndex: 'year', key: 'year' },
                            { title: 'Month', dataIndex: 'month', key: 'month', render: (v) => MONTHS.find(m => m.value === v)?.label || `Month ${v}` },
                            { title: 'Days Credited', dataIndex: 'days_credited', key: 'days_credited' },
                            { title: 'Before', dataIndex: 'balance_before', key: 'balance_before' },
                            { title: 'After', dataIndex: 'balance_after', key: 'balance_after' },
                            { title: 'Pro-rated', dataIndex: 'was_prorated', key: 'was_prorated', render: (v) => v ? <Tag color="blue">Yes</Tag> : 'No' },
                            { title: 'Capped', dataIndex: 'was_capped', key: 'was_capped', render: (v) => v ? <Tag color="orange">Yes</Tag> : 'No' },
                            { title: 'Generated By', dataIndex: 'triggered_by_name', key: 'triggered_by_name', render: (v) => v || 'System' },
                            { title: 'Generated At', dataIndex: 'created_at', key: 'created_at', render: (v) => v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—' },
                        ]}
                        dataSource={creditLogs}
                        loading={creditLogLoading}
                        rowKey="id"
                        pagination={{
                            ...creditLogPagination,
                            showSizeChanger: !isMobile,
                            showQuickJumper: !isMobile,
                            simple: isMobile,
                            size: isMobile ? 'small' : 'default',
                            onChange: pageSetter('logPage', 'logPageSize', logPageSize),
                        }}
                        size={isMobile ? 'small' : 'middle'}
                        scroll={{ x: 1100 }}
                    />
                </div>
            ),
        },
    ];

    const handleTabChange = (key) => setFilters({ tab: key }, { replace: false });

    // ─── Modal content renderer ───────────────────────────────────────
    const renderModalContent = () => {
        // View application
        if (modalType === 'viewApp' && selectedRecord) {
            const r = selectedRecord;
            return (
                <div>
                    <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Employee" span={2}>{r.employee_name}</Descriptions.Item>
                        <Descriptions.Item label="Leave Type">{r.leave_type_name}</Descriptions.Item>
                        <Descriptions.Item label="Status"><Tag color={getStatusColor(r.status)}>{(r.status || '').toUpperCase()}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Start Date">{dayjs(r.start_date).format('DD MMM YYYY')}</Descriptions.Item>
                        <Descriptions.Item label="End Date">{dayjs(r.end_date).format('DD MMM YYYY')}</Descriptions.Item>
                        <Descriptions.Item label="Total Days">{r.total_days}</Descriptions.Item>
                        <Descriptions.Item label="Leave Duration">{(r.leave_duration || '').replace(/_/g, ' ')}</Descriptions.Item>
                        <Descriptions.Item label="Priority">{r.priority}</Descriptions.Item>
                        <Descriptions.Item label="Applied">{dayjs(r.created_at).format('DD MMM YYYY')}</Descriptions.Item>
                        <Descriptions.Item label="Reason" span={2}>{r.reason}</Descriptions.Item>
                        {r.approved_by_name && <Descriptions.Item label="Approved By">{r.approved_by_name}</Descriptions.Item>}
                        {r.rejection_reason && <Descriptions.Item label="Rejection Reason" span={2}><Text type="danger">{r.rejection_reason}</Text></Descriptions.Item>}
                    </Descriptions>

                    <div style={{ marginTop: 16 }}>
                        <Text strong style={{ fontSize: 14 }}>Employee Leave Balances ({dayjs(r.start_date).year()})</Text>
                        <Table
                            columns={[
                                { title: 'Leave Type', dataIndex: 'leave_type_name', key: 'lt', width: 130 },
                                { title: 'Allocated', dataIndex: 'allocated_days', key: 'al', width: 80 },
                                { title: 'Used', dataIndex: 'used_days', key: 'us', width: 70 },
                                { title: 'Pending', dataIndex: 'pending_days', key: 'pe', width: 70, render: (v) => parseFloat(v || 0) > 0 ? <Tag color="orange">{v}</Tag> : v },
                                { title: 'Available', dataIndex: 'available_days', key: 'av', width: 80, render: (v) => <Text strong style={{ color: '#3f8600' }}>{v}</Text> },
                            ]}
                            dataSource={viewEmpBalances}
                            loading={viewEmpBalLoading}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            style={{ marginTop: 8 }}
                            scroll={{ x: 430 }}
                        />
                    </div>

                    {r.leave_type_category === 'comp' && (
                        <div style={{ marginTop: 16 }}>
                            <Text strong style={{ fontSize: 14 }}>Comp-off Balance:</Text>{' '}
                            {viewEmpCompOffAvail === null ? (
                                <Tag>Loading...</Tag>
                            ) : (
                                <Tag color={viewEmpCompOffAvail >= parseFloat(r.total_days || 0) ? 'green' : 'red'}>
                                    {viewEmpCompOffAvail} day(s) available
                                </Tag>
                            )}
                            {viewEmpCompOffAvail !== null && viewEmpCompOffAvail < parseFloat(r.total_days || 0) && (
                                <Text type="danger" style={{ marginLeft: 8 }}>
                                    Requested {r.total_days} day(s) — insufficient balance.
                                </Text>
                            )}
                        </div>
                    )}

                    {r.status === 'pending' && (
                        <div style={{ marginTop: 16 }}>
                            <Text strong style={{ fontSize: 14 }}>Action</Text>
                            <Input.TextArea
                                rows={2}
                                placeholder="Enter reason for approval or rejection (required)"
                                value={actionReason}
                                onChange={(e) => setActionReason(e.target.value)}
                                style={{ marginTop: 8 }}
                            />
                            <Space style={{ marginTop: 12 }}>
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    loading={actionLoading}
                                    onClick={() => handleApprove(r.id, actionReason)}
                                >
                                    Approve
                                </Button>
                                <Button
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    loading={actionLoading}
                                    onClick={() => handleReject(r.id, actionReason)}
                                >
                                    Reject
                                </Button>
                            </Space>
                        </div>
                    )}
                </div>
            );
        }

        // Create/Edit application
        if (modalType === 'createApp' || modalType === 'editApp') {
            return (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {modalType === 'createApp' && (
                        <Form.Item label="Employee" name="employee" rules={[{ required: true, message: 'Select employee' }]}>
                            <Select
                                placeholder="Select Employee"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {employees.map(emp => {
                                    const name = emp.user_name || emp.user_email || 'Unnamed';
                                    const label = emp.employee_id ? `${name} (${emp.employee_id})` : name;
                                    return (
                                        <Option key={emp.id} value={emp.id}>
                                            {label}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                    )}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Leave Type" name="leave_type" rules={[{ required: true, message: 'Select leave type' }]}>
                                <Select placeholder="Select Leave Type">
                                    {leaveTypes.map(t => <Option key={t.id} value={t.id}>{t.name}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Leave Duration" name="leave_duration" initialValue="full_day">
                                <Select>
                                    <Option value="full_day">Full Day</Option>
                                    <Option value="half_day_morning">Half Day - Morning</Option>
                                    <Option value="half_day_afternoon">Half Day - Afternoon</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Leave Dates" name="leave_dates" rules={[{ required: true, message: 'Select dates' }]}>
                        <RangePicker
                            style={{ width: '100%' }}
                            disabledDate={(current) => {
                                if (user?.is_staff || user?.is_superuser) return false;
                                return current && current < dayjs().startOf('day');
                            }}
                        />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Priority" name="priority" initialValue="normal">
                                <Select>
                                    <Option value="low">Low</Option>
                                    <Option value="normal">Normal</Option>
                                    <Option value="high">High</Option>
                                    <Option value="urgent">Urgent</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Contact Number" name="contact_number">
                                <Input placeholder="Contact during leave" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Reason" name="reason" rules={[{ required: true, message: 'Enter reason' }]}>
                        <TextArea rows={3} placeholder="Reason for leave" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                {modalType === 'editApp' ? 'Update' : 'Submit'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            );
        }

        // Create/Edit leave type
        if (modalType === 'createType' || modalType === 'editType') {
            const infoIcon = (tip) => (
                <Tooltip title={tip}>
                    <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.35)', marginLeft: 4 }} />
                </Tooltip>
            );
            return (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label={<span>Name {infoIcon('A unique display name for this leave type, e.g. Casual Leave, Sick Leave')}</span>} name="name" rules={[{ required: true }]}>
                                <Input placeholder="e.g. Casual Leave" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label={<span>Category {infoIcon('Classification of leave — determines how this leave type is treated in policies and reports')}</span>} name="category" rules={[{ required: true }]}>
                                <Select placeholder="Select category">
                                    {Object.entries(LEAVE_CATEGORY_LABELS).map(([k, v]) => (
                                        <Option key={k} value={k}>{v}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label={<span>Description {infoIcon('Optional details about when and how this leave type should be used')}</span>} name="description">
                        <TextArea rows={2} />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label={<span>Max Days/Year {infoIcon('Total maximum leave days an employee can avail in a calendar year for this type')}</span>} name="max_days_per_year" rules={[{ required: true }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label={<span>Monthly Credit {infoIcon('Number of leave days automatically credited to an employee each month (e.g. 1.5 days/month for 18 days/year)')}</span>} name="monthly_credit_days" initialValue={0}>
                                <InputNumber min={0} step={0.25} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label={<span>Max Consecutive {infoIcon('Maximum number of consecutive days an employee can take off at a stretch in a single application')}</span>} name="max_consecutive_days" initialValue={30}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label={<span>Max Application Days {infoIcon('Maximum total days allowed in a single leave application — prevents excessively long leave requests')}</span>} name="max_application_days" initialValue={48}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label={<span>Min Notice Days {infoIcon('Minimum number of days in advance an employee must apply before the leave start date')}</span>} name="min_notice_days" initialValue={1}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label={<span>Max Accumulation {infoIcon('Upper limit on total accumulated leave balance — set to 0 for unlimited accumulation')}</span>} name="max_accumulation_days" initialValue={0}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="is_carry_forward_allowed" valuePropName="checked" initialValue={false}>
                                <Checkbox>Carry Forward {infoIcon('Allow unused leave balance to be carried over to the next calendar year')}</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="is_encashable" valuePropName="checked" initialValue={false}>
                                <Checkbox>Encashable {infoIcon('Allow employees to convert unused leave days into monetary compensation')}</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="allows_half_day" valuePropName="checked" initialValue={false}>
                                <Checkbox>Half Day {infoIcon('Allow employees to apply for half-day (morning or afternoon) leaves of this type')}</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.is_carry_forward_allowed !== cur.is_carry_forward_allowed}>
                        {({ getFieldValue }) => getFieldValue('is_carry_forward_allowed') ? (
                            <Form.Item label={<span>Max Carry Forward Days {infoIcon('Maximum number of unused leave days that can be carried forward to the next year')}</span>} name="max_carry_forward_days" initialValue={0}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        ) : null}
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.is_encashable !== cur.is_encashable}>
                        {({ getFieldValue }) => getFieldValue('is_encashable') ? (
                            <Form.Item label={<span>Max Encashable Days {infoIcon('Maximum number of leave days that can be encashed (converted to cash) per year')}</span>} name="max_encashable_days" initialValue={0}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        ) : null}
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                {modalType === 'editType' ? 'Update' : 'Create'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            );
        }

        // Create/Edit holiday
        if (modalType === 'createHoliday' || modalType === 'editHoliday') {
            return (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                                <Input placeholder="e.g. Republic Day" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Date" name="date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Holiday Type" name="holiday_type" rules={[{ required: true }]}>
                                <Select placeholder="Select type">
                                    {Object.entries(HOLIDAY_TYPE_LABELS).map(([k, v]) => (
                                        <Option key={k} value={k}>{v}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Description" name="description">
                        <TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="is_recurring" valuePropName="checked" initialValue={false}>
                        <Checkbox>Is Recurring</Checkbox>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.is_recurring !== cur.is_recurring}>
                        {({ getFieldValue }) => getFieldValue('is_recurring') ? (
                            <Form.Item label="Recurrence Rule" name="recurrence_rule">
                                <RRuleBuilder />
                            </Form.Item>
                        ) : null}
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                {modalType === 'editHoliday' ? 'Update' : 'Create'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            );
        }

        // Create encashment
        if (modalType === 'createEncashment') {
            return (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Leave Type" name="leave_type" rules={[{ required: true }]}>
                                <Select placeholder="Select Leave Type">
                                    {leaveTypes.filter(t => t.is_encashable).map(t => (
                                        <Option key={t.id} value={t.id}>{t.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Year" name="year" initialValue={dayjs().year()} rules={[{ required: true }]}>
                                <InputNumber min={2020} max={2099} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Days to Encash" name="days_to_encash" rules={[{ required: true }]}>
                                <InputNumber min={0.5} step={0.5} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Rate per Day" name="rate_per_day" rules={[{ required: true }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Reason" name="reason" rules={[{ required: true }]}>
                        <TextArea rows={2} placeholder="Reason for encashment" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button onClick={closeModal}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={saving}>Submit</Button>
                        </Space>
                    </Form.Item>
                </Form>
            );
        }

        return null;
    };

    const getModalTitle = () => {
        const titles = {
            createApp: 'New Leave Application',
            editApp: 'Edit Leave Application',
            viewApp: 'Leave Application Details',
            createType: 'Add Leave Type',
            editType: 'Edit Leave Type',
            createHoliday: 'Add Holiday',
            editHoliday: 'Edit Holiday',
            createEncashment: 'New Encashment Request',
        };
        return titles[modalType] || '';
    };

    // ─── Render ───────────────────────────────────────────────────────
    return (
        <div style={{ padding: isMobile ? '12px' : '0 24px 24px 24px' }}>
            <div style={{ marginBottom: isMobile ? 16 : 24 }}>
                <Title level={isMobile ? 4 : 3}>Leave Management</Title>
            </div>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card><Statistic title="Total Applications" value={stats.total} prefix={<FileTextOutlined />} valueStyle={{ color: '#1890ff' }} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card><Statistic title="Pending" value={stats.pending} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#faad14' }} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card><Statistic title="Approved" value={stats.approved} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card><Statistic title="Rejected" value={stats.rejected} prefix={<CloseCircleOutlined />} valueStyle={{ color: '#f5222d' }} /></Card>
                </Col>
            </Row>

            <Card>
                <Tabs items={tabItems} activeKey={activeTab} onChange={handleTabChange} />
            </Card>

            <Modal
                title={getModalTitle()}
                open={modalVisible}
                onCancel={closeModal}
                footer={modalType === 'viewApp' ? [<Button key="close" onClick={closeModal}>Close</Button>] : null}
                width={modalType === 'viewApp' ? 750 : modalType === 'createType' || modalType === 'editType' ? 700 : 600}
                destroyOnClose
            >
                {renderModalContent()}
            </Modal>
        </div>
    );
};

export default LeaveManagement;
